import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CRMContext } from './CRMContext';
import {
  addInvestorToBoard as addInvestorToBoardRequest,
  addNewColumn as addNewColumnRequest,
  createBoard as createBoardRequest,
  deleteBoard as deleteBoardRequest,
  deleteColumn as deleteColumnRequest,
  fetchInvestorById,
  fetchInvestorsByName,
  fetchInvestorsPage,
  fetchUserBoards,
  removeInvestorFromBoard as removeInvestorFromBoardRequest,
  renameColumn as renameColumnRequest,
  updateBoardName as updateBoardNameRequest,
  updateBoardColumns as updateBoardColumnsRequest,
  updateColumnOrder as updateColumnOrderRequest,
  updateInvestorNote as updateInvestorNoteRequest,
  updateInvestorPosition as updateInvestorPositionRequest,
  updateInvestorValue as updateInvestorValueRequest,
  updateTargetAmountForBoard as updateTargetAmountForBoardRequest,
} from './api';
import {
  findInvestorColumnId,
  getAllBoardInvestorIds,
  getBoardById,
  getBoardResearchInvestorsCount,
  getBoardTotalInvestorValue,
  isInvestorInBoard,
  mergeBoardInvestorDetails,
  moveInvestorBetweenColumns,
  normalizeBoards,
  persistSelectedBoardId,
  readStoredSelectedBoardId,
  sortColumnsByPosition,
} from './helpers';

function buildFallbackInvestor(investorId, investor) {
  return {
    _id: investorId,
    id: investor?.id ?? investorId,
    name: investor?.name || 'Unnamed Investor',
    country: investor?.country || '',
    type: Array.isArray(investor?.type) ? investor.type : [],
    industries: investor?.industries,
    short_info: investor?.short_info,
  };
}

function normalizeInvestorId(investor) {
  if (typeof investor === 'string' || typeof investor === 'number') {
    return String(investor);
  }

  return investor?._id || investor?.investor_id || investor?.id || null;
}

export function CRMProvider({ children }) {
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [investorDetailsMap, setInvestorDetailsMap] = useState({});

  const boardsRef = useRef(boards);
  const selectedBoardIdRef = useRef(selectedBoardId);
  const investorDetailsRef = useRef(investorDetailsMap);
  const lastFetchIdRef = useRef(0);

  useEffect(() => {
    boardsRef.current = boards;
  }, [boards]);

  useEffect(() => {
    selectedBoardIdRef.current = selectedBoardId;
  }, [selectedBoardId]);

  useEffect(() => {
    investorDetailsRef.current = investorDetailsMap;
  }, [investorDetailsMap]);

  const ensureInvestorDetails = useCallback(async (investorIds = []) => {
    const missingIds = [...new Set(
      investorIds
        .map((investorId) => String(investorId || '').trim())
        .filter(Boolean)
        .filter((investorId) => !investorDetailsRef.current[investorId])
    )];

    if (missingIds.length === 0) {
      return investorDetailsRef.current;
    }

    const fetchedInvestors = await Promise.all(missingIds.map(fetchInvestorById));
    const nextDetails = { ...investorDetailsRef.current };

    missingIds.forEach((investorId, index) => {
      nextDetails[investorId] = fetchedInvestors[index] || buildFallbackInvestor(investorId);
    });

    investorDetailsRef.current = nextDetails;
    setInvestorDetailsMap(nextDetails);

    return nextDetails;
  }, []);

  const hydrateBoards = useCallback(async (rawBoards = []) => {
    const normalizedBoards = normalizeBoards(rawBoards);
    const investorIds = getAllBoardInvestorIds(normalizedBoards);
    const details = await ensureInvestorDetails(investorIds);

    return mergeBoardInvestorDetails(normalizedBoards, details);
  }, [ensureInvestorDetails]);

  const applySelection = useCallback((nextBoards, preferredBoardId = null) => {
    if (!Array.isArray(nextBoards) || nextBoards.length === 0) {
      setSelectedBoardId(null);
      persistSelectedBoardId(null);
      return null;
    }

    const candidateIds = [
      preferredBoardId,
      selectedBoardIdRef.current,
      readStoredSelectedBoardId(),
      nextBoards[0]?.board_id,
    ].filter((value) => value != null);

    const nextBoardId = candidateIds.find((candidateId) =>
      nextBoards.some((board) => String(board?.board_id) === String(candidateId))
    );

    setSelectedBoardId(nextBoardId ?? nextBoards[0]?.board_id ?? null);
    persistSelectedBoardId(nextBoardId ?? nextBoards[0]?.board_id ?? null);

    return nextBoardId ?? nextBoards[0]?.board_id ?? null;
  }, []);

  const refreshBoards = useCallback(async ({ preferredBoardId = null } = {}) => {
    const fetchId = ++lastFetchIdRef.current;

    setRefreshing(true);

    try {
      const fetchedBoards = await fetchUserBoards();
      const hydratedBoards = await hydrateBoards(fetchedBoards);

      if (fetchId !== lastFetchIdRef.current) {
        return hydratedBoards;
      }

      setBoards(hydratedBoards);
      applySelection(hydratedBoards, preferredBoardId);
      setError(null);

      return hydratedBoards;
    } catch (nextError) {
      if (fetchId === lastFetchIdRef.current) {
        setError(nextError);
      }
      throw nextError;
    } finally {
      if (fetchId === lastFetchIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [applySelection, hydrateBoards]);

  useEffect(() => {
    refreshBoards().catch((nextError) => {
      console.error('Error loading CRM boards:', nextError);
    });
  }, [refreshBoards]);

  const replaceBoardState = useCallback((boardId, updater) => {
    setBoards((prevBoards) =>
      prevBoards.map((board) => (
        String(board?.board_id) === String(boardId)
          ? updater(board)
          : board
      ))
    );
  }, []);

  const selectBoard = useCallback((boardOrId) => {
    const nextBoardId = typeof boardOrId === 'object'
      ? boardOrId?.board_id ?? null
      : boardOrId;

    setSelectedBoardId(nextBoardId ?? null);
    persistSelectedBoardId(nextBoardId ?? null);
  }, []);

  const selectedBoard = useMemo(
    () => getBoardById(boards, selectedBoardId),
    [boards, selectedBoardId]
  );

  const selectedBoardColumns = useMemo(
    () => sortColumnsByPosition(selectedBoard?.columns || []),
    [selectedBoard]
  );

  const totalBoards = boards.length;

  const totalInvestorsInResearch = useMemo(
    () => boards.reduce((total, board) => total + getBoardResearchInvestorsCount(board), 0),
    [boards]
  );

  const selectedBoardTotalInvestorValues = useMemo(
    () => getBoardTotalInvestorValue(selectedBoard),
    [selectedBoard]
  );

  const getInvestorColumnId = useCallback((investorId, boardId = selectedBoardIdRef.current) => {
    const board = getBoardById(boardsRef.current, boardId);
    return findInvestorColumnId(board, investorId);
  }, []);

  const isInvestorInSelectedBoard = useCallback((investorId) => {
    return isInvestorInBoard(selectedBoard, investorId);
  }, [selectedBoard]);

  const isInvestorInBoardById = useCallback((investorId, boardId) => {
    const board = getBoardById(boardsRef.current, boardId);
    return isInvestorInBoard(board, investorId);
  }, []);

  const createBoard = useCallback(async (boardName) => {
    const trimmedBoardName = String(boardName || '').trim();

    if (!trimmedBoardName) {
      return null;
    }

    const response = await createBoardRequest(trimmedBoardName);
    const createdBoardId = response?.data?.board?.board_id ?? response?.data?.board_id ?? null;

    await refreshBoards({ preferredBoardId: createdBoardId });

    return createdBoardId;
  }, [refreshBoards]);

  const renameBoard = useCallback(async (boardId, newName) => {
    const trimmedBoardName = String(newName || '').trim();

    if (!trimmedBoardName) {
      return;
    }

    await updateBoardNameRequest(boardId, trimmedBoardName);
    replaceBoardState(boardId, (board) => ({ ...board, board_name: trimmedBoardName }));
  }, [replaceBoardState]);

  const deleteBoard = useCallback(async (boardId) => {
    await deleteBoardRequest(boardId);

    const updatedBoards = boardsRef.current.filter(
      (board) => String(board?.board_id) !== String(boardId)
    );

    setBoards(updatedBoards);
    applySelection(
      updatedBoards,
      String(selectedBoardIdRef.current) === String(boardId)
        ? updatedBoards[0]?.board_id ?? null
        : selectedBoardIdRef.current
    );
  }, [applySelection]);

  const setTargetAmount = useCallback(async (boardId, targetAmount) => {
    await updateTargetAmountForBoardRequest(boardId, targetAmount);
    replaceBoardState(boardId, (board) => ({
      ...board,
      target_amount: Number(targetAmount) || 0,
    }));
  }, [replaceBoardState]);

  const clearTargetAmount = useCallback(async (boardId) => {
    await updateTargetAmountForBoardRequest(boardId, 0);
    replaceBoardState(boardId, (board) => ({
      ...board,
      target_amount: 0,
    }));
  }, [replaceBoardState]);

  const addInvestorToBoard = useCallback(async ({
    boardId = selectedBoardIdRef.current,
    columnId = 1,
    investor,
  }) => {
    const investorId = normalizeInvestorId(investor);

    if (!boardId || !columnId || !investorId) {
      return false;
    }

    await addInvestorToBoardRequest(boardId, columnId, investorId);

    const detailsMap = await ensureInvestorDetails([investorId]);
    const details = detailsMap[investorId] || buildFallbackInvestor(investorId, investor);
    const investorPayload = {
      investor_id: investorId,
      note: '',
      value: 0,
      details,
    };

    replaceBoardState(boardId, (board) => {
      if (isInvestorInBoard(board, investorId)) {
        return board;
      }

      return {
        ...board,
        columns: board.columns.map((column) => (
          String(column?.column_id) === String(columnId)
            ? { ...column, investors: [...column.investors, investorPayload] }
            : column
        )),
      };
    });

    return true;
  }, [ensureInvestorDetails, replaceBoardState]);

  const removeInvestorFromBoard = useCallback(async ({
    boardId = selectedBoardIdRef.current,
    columnId,
    investorId,
  }) => {
    const resolvedColumnId = columnId ?? getInvestorColumnId(investorId, boardId);

    if (!boardId || !resolvedColumnId || !investorId) {
      return false;
    }

    await removeInvestorFromBoardRequest(boardId, resolvedColumnId, investorId);
    replaceBoardState(boardId, (board) => ({
      ...board,
      columns: board.columns.map((column) => (
        String(column?.column_id) === String(resolvedColumnId)
          ? {
            ...column,
            investors: column.investors.filter(
              (currentInvestor) => String(currentInvestor?.investor_id) !== String(investorId)
            ),
          }
          : column
      )),
    }));

    return true;
  }, [getInvestorColumnId, replaceBoardState]);

  const moveInvestor = useCallback(async ({
    boardId = selectedBoardIdRef.current,
    sourceColumnId,
    destinationColumnId,
    investorId,
    destinationIndex,
  }) => {
    const previousBoards = boardsRef.current;

    setBoards((prevBoards) =>
      prevBoards.map((board) => (
        String(board?.board_id) === String(boardId)
          ? moveInvestorBetweenColumns(
            board,
            sourceColumnId,
            destinationColumnId,
            investorId,
            destinationIndex
          )
          : board
      ))
    );

    try {
      await updateInvestorPositionRequest(
        boardId,
        sourceColumnId,
        destinationColumnId,
        investorId,
        destinationIndex + 1
      );
    } catch (nextError) {
      setBoards(previousBoards);
      throw nextError;
    }
  }, []);

  const updateInvestorNote = useCallback(async ({
    boardId = selectedBoardIdRef.current,
    columnId,
    investorId,
    note,
  }) => {
    await updateInvestorNoteRequest(boardId, columnId, investorId, note);
    replaceBoardState(boardId, (board) => ({
      ...board,
      columns: board.columns.map((column) => (
        String(column?.column_id) === String(columnId)
          ? {
            ...column,
            investors: column.investors.map((investor) => (
              String(investor?.investor_id) === String(investorId)
                ? { ...investor, note: note || '' }
                : investor
            )),
          }
          : column
      )),
    }));
  }, [replaceBoardState]);

  const updateInvestorValue = useCallback(async ({
    boardId = selectedBoardIdRef.current,
    columnId,
    investorId,
    value,
  }) => {
    await updateInvestorValueRequest(boardId, columnId, investorId, value);
    replaceBoardState(boardId, (board) => ({
      ...board,
      columns: board.columns.map((column) => (
        String(column?.column_id) === String(columnId)
          ? {
            ...column,
            investors: column.investors.map((investor) => (
              String(investor?.investor_id) === String(investorId)
                ? { ...investor, value: value ?? 0 }
                : investor
            )),
          }
          : column
      )),
    }));
  }, [replaceBoardState]);

  const addColumn = useCallback(async (boardId, columnName) => {
    const trimmedColumnName = String(columnName || '').trim();

    if (!trimmedColumnName) {
      return;
    }

    await addNewColumnRequest(boardId, trimmedColumnName);
    await refreshBoards({ preferredBoardId: boardId });
  }, [refreshBoards]);

  const saveBoardColumns = useCallback(async (boardId, columns) => {
    const nextColumns = (columns || []).map((column, index) => ({
      ...column,
      column_name: String(column?.column_name || '').trim(),
      position: index + 1,
    }));

    await updateBoardColumnsRequest(boardId, nextColumns);
    await refreshBoards({ preferredBoardId: boardId });
  }, [refreshBoards]);

  const searchAvailableInvestors = useCallback(async ({
    boardId = selectedBoardIdRef.current,
    query = '',
    excludeBoardInvestors = true,
  } = {}) => {
    const board = getBoardById(boardsRef.current, boardId);
    const usedInvestorIds = new Set(
      (board?.columns || []).flatMap((column) =>
        (column?.investors || []).map((investor) => String(investor?.investor_id || ''))
      )
    );

    const investors = query.trim()
      ? await fetchInvestorsByName(query.trim())
      : await fetchInvestorsPage();

    if (!excludeBoardInvestors) {
      return investors;
    }

    return investors.filter((investor) => !usedInvestorIds.has(String(investor?._id || '')));
  }, []);

  const value = useMemo(() => ({
    boards,
    selectedBoardId,
    selectedBoard,
    selectedBoardColumns,
    totalBoards,
    totalInvestorsInResearch,
    selectedBoardTotalInvestorValues,
    investorDetailsMap,
    loading,
    refreshing,
    error,
    selectBoard,
    refreshBoards,
    createBoard,
    renameBoard,
    deleteBoard,
    setTargetAmount,
    clearTargetAmount,
    addInvestorToBoard,
    removeInvestorFromBoard,
    moveInvestor,
    updateInvestorNote,
    updateInvestorValue,
    addColumn,
    saveBoardColumns,
    searchAvailableInvestors,
    getInvestorColumnId,
    isInvestorInSelectedBoard,
    isInvestorInBoard: isInvestorInBoardById,
  }), [
    addColumn,
    addInvestorToBoard,
    boards,
    clearTargetAmount,
    createBoard,
    deleteBoard,
    error,
    getInvestorColumnId,
    investorDetailsMap,
    isInvestorInBoardById,
    isInvestorInSelectedBoard,
    loading,
    moveInvestor,
    refreshBoards,
    refreshing,
    removeInvestorFromBoard,
    renameBoard,
    saveBoardColumns,
    searchAvailableInvestors,
    selectBoard,
    selectedBoard,
    selectedBoardColumns,
    selectedBoardId,
    selectedBoardTotalInvestorValues,
    setTargetAmount,
    totalBoards,
    totalInvestorsInResearch,
    updateInvestorNote,
    updateInvestorValue,
  ]);

  return (
    <CRMContext.Provider value={value}>
      {children}
    </CRMContext.Provider>
  );
}

export function useCRM() {
  const context = useContext(CRMContext);

  if (!context) {
    throw new Error('useCRM must be used within a CRMProvider');
  }

  return context;
}
